import React from "react";
import { Row, Col } from "react-bootstrap";
import { object, number, array, bool } from "prop-types";
import StrengthsIcon from "resources/images/dynamic_score_reports/personality/score-report/Strengths-icon.png";
import PotentialStrengthsIcon from "resources/images/dynamic_score_reports/personality/score-report/PotentialStrengths-icon.png";
import NeedsAttentionsIcon from "resources/images/dynamic_score_reports/personality/score-report/NeedsAttention-icon.png";
import SalesSuccessIcon from "resources/images/dynamic_score_reports/personality/score-report/sales-icon.png";
import MotivationIcon from "resources/images/dynamic_score_reports/personality/score-report/motivation-icon.png";
import WorkStrengthsIcon from "resources/images/dynamic_score_reports/personality/score-report/work-icon.png";
import InterpersonalStrengthsIcon from "resources/images/dynamic_score_reports/personality/score-report/interpersonal-icon.png";
import InnerResourcesIcon from "resources/images/dynamic_score_reports/personality/score-report/inner-icon.png";
import NumberedScoreScale from "components/DynamicScoreReportScales/NumberedScoreScale";
import {
  getScaleDetailSections,
  getAssessmentErrorAlert,
} from "../ScoreReportHelpers";

const SalesAssessment = ({
  isInvalidTest,
  reportText,
  SelectedScoreRangeSection,
  SubcategoryScoreColumns,
  testId,
  testScores,
}) => {
  const traitAbbrevsToLabel = {
    15: {
      PDL: "Diplomacy in relating to others",
      COP: "Cooperative attitude",
      PAT: "Patience",
      RLX: "Relaxed attitude",
      S: "Interest in being helpful to others",
      AST: "Ability to restrain assertiveness in relating to others",
      CMP: "Ability to be non-competitive when relating to others",
      ACH: "Focus on achievement and getting things done",
    },
    24: {
      SAL: "Sales Disposition",
      CC: "Initiative (Cold Calling)",
      CLS: "Sales Closing",
      ACH: "Achievement",
      CMP: "Competitiveness",
    },
  };

  // ---------------------------- Overview ----------------------------
  const getOverview = () => {
    const imageSrcMap = {
      Strengths: {
        label: "Strength",
        imgSrc: StrengthsIcon,
      },
      "Potential Strengths": {
        label: "Potential Strength",
        imgSrc: PotentialStrengthsIcon,
      },
      "Needs Attention": {
        label: "Needs Attention",
        imgSrc: NeedsAttentionsIcon,
      },
    };

    const overviewKey = Object.values(imageSrcMap).map(({ label, imgSrc }) => (
      <li key={label} className="font-weight-bold">
        <img
          src={imgSrc}
          alt={`${label} Key`}
          className="sales-strengths-icons mr-1"
        />{" "}
        = {label}
      </li>
    ));

    const overviewList = [];

    Object.keys(traitAbbrevsToLabel[testId]).forEach((traitAbbrev) => {
      const traitValue = reportText.get("summary").get(traitAbbrev);
      overviewList.push(
        <li key={traitAbbrev}>
          <img
            src={imageSrcMap[traitValue].imgSrc}
            alt={traitValue}
            className="sales-strengths-icons mr-1"
          />{" "}
          {traitAbbrevsToLabel[testId][traitAbbrev]}
        </li>
      );
    });

    return (
      <div>
        <h3 className="reportHeader mt-3">Overview</h3>
        <div className="test-report-sales-assessment-overview">
          <div className="test-report-sales-assessment-overview-key">
            <ul className="pl-1">{overviewKey}</ul>
          </div>
          <div className="test-report-sales-assessment-overview-lists">
            <ul>{overviewList}</ul>
          </div>
        </div>
        {SelectedScoreRangeSection && <hr className="divider" />}
        {SelectedScoreRangeSection}
        <hr className="divider mt-4" />
      </div>
    );
  };

  // ---------------------------- Score Details ----------------------------
  const getScoreDetails = () => {
    if (!testScores || !(reportText && reportText.get("traits"))) {
      return null;
    }

    const tableStructure = {
      "Sales Success": {
        imgSrc: SalesSuccessIcon,
        abbrevArr: ["SAL", "CC", "CLS"],
      },
      "Motivation & Achievement": {
        imgSrc: MotivationIcon,
        abbrevArr: ["ACH", "CMP", "MOT", "GO"],
      },
      "Work Strengths": {
        imgSrc: WorkStrengthsIcon,
        abbrevArr: ["PLN", "INI", "TMP", "MGT"],
      },
      "Interpersonal Strengths": {
        imgSrc: InterpersonalStrengthsIcon,
        abbrevArr: ["AST", "PDL", "EXT", "COP"],
      },
      "Inner Resources": {
        imgSrc: InnerResourcesIcon,
        abbrevArr: ["RLX", "PAT", "SCN"],
      },
    };

    const rows = Object.entries(tableStructure).map(
      ([title, { imgSrc, abbrevArr }]) => (
        <tr key={title}>
          <td>
            <img
              alt={title}
              src={imgSrc}
              style={{ height: "64px", width: "64px" }}
            />
          </td>
          <td className="font-weight-bold">{title}</td>
          <td className="pt-4 pb-2">
            {abbrevArr.map((abbrev) => (
              <React.Fragment key={abbrev}>
                {getScoreScaleFromAbbrev(abbrev)}
              </React.Fragment>
            ))}
          </td>
        </tr>
      )
    );

    return (
      <div>
        <h3 className="reportHeader mt-2">Score Details</h3>
        <table className="score-details-table">
          <colgroup>
            <col span="1" style={{ width: "10%" }} />
            <col span="1" style={{ width: "10%" }} />
            <col span="1" style={{ width: "100%" }} />
          </colgroup>
          <tbody>{rows}</tbody>
        </table>
        <hr className="divider mt-4" />
      </div>
    );
  };

  const getScoreScaleFromAbbrev = (abbrev) => {
    const traitInfo = reportText.getIn(["traits", abbrev]);
    const range = reportText.getIn(["traits", abbrev, "range"]);
    const actualScore = testScores.get(`${abbrev}_adjPercentile`);
    const scaleProps = {
      score: actualScore,
      maxScore: 100,
      cellSpan: 10,
      hideNumbers: true,
    };

    if (range && range.size > 1) {
      scaleProps.hasScoreRange = true;
      scaleProps.scoreRangeMin = range.first();
      scaleProps.scoreRangeMax = range.last();
    }

    return (
      <li className="d-flex">
        <Row className="w-100">
          <Col xs={12} sm={3} md={3} lg={3} className="text-right">
            {traitInfo.get("name")}
          </Col>
          <Col xs={12} sm={9} md={9} lg={9} className="score-bars">
            <NumberedScoreScale {...scaleProps} />
          </Col>
        </Row>
      </li>
    );
  };

  // ---------------------------- Validity and Response Style ----------------------------
  const getValidityAndResponseStyle = () => (
    <div>
      <hr className="divider mt-4" />
      {isInvalidTest &&
        getAssessmentErrorAlert(testId, testScores.get("INC_score"))}
      <h3 className="reportHeader my-2">Validity and Response Style</h3>
      <p>{reportText.get("validity_text")}</p>
      <Row style={{ margin: "0 2px 0 2px" }}>{SubcategoryScoreColumns}</Row>
    </div>
  );

  return reportText ? (
    <div>
      {getOverview()}
      {getScoreDetails()}
      {getScaleDetailSections(testId, reportText)}
      {getValidityAndResponseStyle()}
      <p className="ml-1">
        {testId === 15 ? "CSAP" : "SalesAP"} &copy;2002, Multi-Health Systems,
        Inc. All rights reserved.
      </p>
    </div>
  ) : null;
};

SalesAssessment.propTypes = {
  isInvalidTest: bool,
  testId: number,
  reportText: object,
  SubcategoryScoreColumns: array,
  SelectedScoreRangeSection: object,
  testScores: object,
};

export default SalesAssessment;
